import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { Loader2, Clock, Package } from 'lucide-react';

// Polls the bulk-trace job list every 5s and surfaces a small live indicator
// in the topbar whenever the current user has a job in `running` or `queued`
// state. Click → navigate to /inventory and switch to Processed Records (we
// pass the tab via location.hash so InventoryTracer can pick it up on mount).
//
// Visible to anyone who can submit bulk traces (i.e., everyone with the
// `inventory` feature). Polling auto-stops when no live jobs are present so
// idle users incur zero overhead — only the 5s ticker stays alive.
//
// Also fires a one-shot browser desktop Notification when a job belonging to
// the current user transitions to `completed` / `failed` / `cancelled` while
// the page isn't focused. We track which job_ids have already been
// notified in localStorage so we don't double-fire across re-mounts /
// reloads.
const POLL_INTERVAL_MS = 5000;
const NOTIFIED_STORAGE_KEY = 'inventory_bulk_notified_job_ids';
const NOTIFY_GRACE_MINUTES = 30;  // don't notify for jobs older than this

const loadNotifiedSet = () => {
  try {
    const raw = localStorage.getItem(NOTIFIED_STORAGE_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
};

const saveNotifiedSet = (set) => {
  try {
    // Cap at 200 ids to avoid unbounded growth — oldest entries fall off.
    const arr = Array.from(set).slice(-200);
    localStorage.setItem(NOTIFIED_STORAGE_KEY, JSON.stringify(arr));
  } catch {
    // localStorage full — silently drop, will re-notify next time which is
    // a much better failure mode than crashing the topbar.
  }
};

const fireDesktopNotification = (job, navigate) => {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;
  const titleByStatus = {
    completed: '✓ Bulk trace finished',
    failed: '✗ Bulk trace failed',
    cancelled: '⊘ Bulk trace cancelled',
  };
  const bodyByStatus = {
    completed: `${job.total} barcode${job.total === 1 ? '' : 's'} processed — click to download Excel`,
    failed: `${job.total} barcode${job.total === 1 ? '' : 's'} job failed: ${job.error || 'unknown error'}`,
    cancelled: `Queued ${job.total}-barcode job was cancelled before it ran`,
  };
  try {
    const n = new Notification(titleByStatus[job.status] || 'Bulk trace update', {
      body: bodyByStatus[job.status] || '',
      icon: '/favicon.ico',
      tag: `inventory-bulk-${job.job_id}`,  // de-dupe if browser shows the same one twice
    });
    n.onclick = () => {
      try { window.focus(); } catch { /* noop */ }
      n.close();
      navigate('/inventory#processed');
    };
  } catch {
    // Some browsers throw on Notification() construction in obscure contexts
    // (e.g. Safari iframe). Silent — the topbar pill is the fallback.
  }
};

export const BulkJobIndicator = () => {
  const { currentUser, api } = useApp();
  const navigate = useNavigate();
  const [liveJob, setLiveJob] = useState(null);  // {job_id, total, progress, status}
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  const intervalRef = useRef(null);
  const notifiedRef = useRef(loadNotifiedSet());

  // Idempotently ask for notification permission the first time the user has
  // a live bulk job. We don't prompt on app boot — too pushy for users who
  // never use bulk trace.
  const requestPermissionIfNeeded = useCallback(async () => {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'granted' || Notification.permission === 'denied') {
      setPermission(Notification.permission);
      return;
    }
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
    } catch {
      // Some browsers reject the call when triggered without user gesture —
      // we'll re-attempt next time the user clicks the indicator.
    }
  }, []);

  const fetchJobs = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const res = await api.bulkTraceListJobs(20);
      const jobs = res.data?.jobs || [];
      // Filter to the current user's own jobs — admins get the global list,
      // but they shouldn't be alerted by traces they didn't start.
      const mine = jobs.filter(j => j.requester_id === currentUser.id);

      // 1) Pick the newest still-live job to drive the topbar pill.
      const live = mine.find(j => j.status === 'running' || j.status === 'queued');
      setLiveJob(live || null);

      // 2) Detect transitions to a terminal state since we last polled and
      //    fire desktop notifications for them. Skip jobs older than the
      //    grace window — those are stale ones from a previous session.
      const now = Date.now();
      const cutoff = now - NOTIFY_GRACE_MINUTES * 60 * 1000;
      let dirty = false;
      for (const j of mine) {
        if (!['completed', 'failed', 'cancelled'].includes(j.status)) continue;
        if (notifiedRef.current.has(j.job_id)) continue;
        const createdMs = j.created_at ? new Date(j.created_at).getTime() : 0;
        if (createdMs && createdMs < cutoff) {
          // Old job — mark as notified so we don't keep checking, but don't
          // actually fire a notification (avoid spam after a fresh login).
          notifiedRef.current.add(j.job_id);
          dirty = true;
          continue;
        }
        // Only fire if the page is currently hidden (otherwise the topbar
        // pill is already enough — desktop popups for foregrounded tabs are
        // annoying).
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
          fireDesktopNotification(j, navigate);
        }
        notifiedRef.current.add(j.job_id);
        dirty = true;
      }
      if (dirty) saveNotifiedSet(notifiedRef.current);
    } catch {
      // Silent — best-effort topbar polling
    }
  }, [api, currentUser?.id, navigate]);

  useEffect(() => {
    if (!currentUser?.id) return undefined;
    fetchJobs();
    intervalRef.current = setInterval(fetchJobs, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [currentUser?.id, fetchJobs]);

  // Ask for permission as soon as the user has their first live job — that's
  // a strong signal they care about the result.
  useEffect(() => {
    if (liveJob && permission === 'default') {
      requestPermissionIfNeeded();
    }
  }, [liveJob, permission, requestPermissionIfNeeded]);

  if (!liveJob) return null;

  const isQueued = liveJob.status === 'queued';
  const pct = liveJob.total > 0 ? Math.round(((liveJob.progress || 0) / liveJob.total) * 100) : 0;
  const label = isQueued
    ? `Queued · ${liveJob.total} barcodes`
    : `Tracing ${liveJob.progress || 0}/${liveJob.total}`;

  return (
    <button
      type="button"
      data-testid="bulk-job-indicator"
      onClick={() => {
        // Re-attempt permission opportunistically — a real click is a user
        // gesture so browsers that previously rejected the bare prompt will
        // accept it now.
        if (permission === 'default') requestPermissionIfNeeded();
        navigate('/inventory#processed');
      }}
      title={`Bulk trace ${isQueued ? 'queued' : 'in progress'} — click to view Processed Records${permission === 'granted' ? '. You\'ll get a desktop notification when it finishes.' : ''}`}
      className={`relative inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium transition-all hover:shadow-sm ${
        isQueued
          ? 'bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100'
          : 'bg-amber-50 border border-amber-200 text-amber-800 hover:bg-amber-100'
      }`}
    >
      {isQueued ? (
        <Clock className="w-3.5 h-3.5" />
      ) : (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      )}
      <Package className="w-3.5 h-3.5 hidden sm:inline" strokeWidth={1.7} />
      <span className="hidden sm:inline">{label}</span>
      <span className="inline sm:hidden">{isQueued ? 'Queued' : `${pct}%`}</span>
      {!isQueued && (
        <span className="hidden md:inline text-amber-600 font-mono">· {pct}%</span>
      )}
    </button>
  );
};
